import { useState, type FormEvent } from "react";
import type { SavedSearch, SavedSearchInput } from "../lib/api";

type Props = {
  initial?: SavedSearch;
  onSubmit: (input: SavedSearchInput) => Promise<void>;
  onCancel: () => void;
};

const CATS = ["pokemon", "magic", "yugioh", "sports", "other"] as const;

export default function SavedSearchForm({ initial, onSubmit, onCancel }: Props) {
  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState(initial?.category ?? "pokemon");
  const [keyword, setKeyword] = useState(initial?.keyword ?? "");
  const [ebayCategoryId, setEbayCategoryId] = useState(initial?.ebay_category_id ?? "");
  // graders/grades are no longer surfaced in the form — the
  // required_title_substring (e.g. "PSA 8") covers both via server-side
  // quoted phrase matching + client-side titleHasAllPhrases. Keeping
  // the existing values on the row when editing so we don't wipe them
  // accidentally; new searches default to null.
  const [graders] = useState<string[] | null>(initial?.graders ?? null);
  const [grades] = useState<string[] | null>(initial?.grades ?? null);
  const [language, setLanguage] = useState((initial?.language ?? []).join(","));
  const [alertThreshold, setAlertThreshold] = useState(initial?.alert_threshold ?? 0.20);
  const [alertWindow, setAlertWindow] = useState(initial?.alert_window_minutes ?? 10);
  const [scanWindow, setScanWindow] = useState(initial?.scan_ending_window_hours ?? 1);
  const [requiredTitle, setRequiredTitle] = useState(
    initial?.required_title_substring ?? "",
  );
  const [webhookUrl, setWebhookUrl] = useState(initial?.webhook_url ?? "");
  const [active, setActive] = useState(initial?.active ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name,
        category,
        keyword,
        ebay_category_id: ebayCategoryId.trim() || null,
        graders,
        grades,
        language: language.trim()
          ? language.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
        alert_threshold: alertThreshold,
        alert_window_minutes: alertWindow,
        scan_ending_window_hours: scanWindow,
        required_title_substring: requiredTitle.trim() || null,
        webhook_url: webhookUrl || null,
        active,
      });
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handle} className="space-y-3 font-mono text-sm">
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
        />
      </Field>
      <Field label="Category">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
        >
          {CATS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </Field>
      <Field label="Keyword">
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          required
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
        />
      </Field>
      <Field label="eBay Category ID (recommended; e.g. 183454 for Pokemon TCG)">
        <input
          value={ebayCategoryId}
          onChange={(e) => setEbayCategoryId(e.target.value)}
          placeholder="183454"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
        />
      </Field>
      <Field label="Language (comma-separated for multi)">
        <input
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          placeholder="english   or   english, japanese"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Alert threshold (0–1)">
          <input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={alertThreshold}
            onChange={(e) => setAlertThreshold(Number(e.target.value))}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
          />
        </Field>
        <Field label="Alert window (min)">
          <input
            type="number"
            min="1"
            value={alertWindow}
            onChange={(e) => setAlertWindow(Number(e.target.value))}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
          />
        </Field>
      </div>
      <Field label="Scan window (hours) — only price auctions ending within this many hours">
        <input
          type="number"
          min="1"
          max="168"
          value={scanWindow}
          onChange={(e) => setScanWindow(Number(e.target.value))}
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
        />
      </Field>
      <Field label='Required in title — phrases that MUST appear in the listing title. Comma-separate multiple (all required). Sent to eBay as quoted phrases AND verified client-side.'>
        <input
          value={requiredTitle}
          onChange={(e) => setRequiredTitle(e.target.value)}
          placeholder='PSA 10, 1st Edition'
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
        />
      </Field>
      <Field label="Webhook URL (optional)">
        <input
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="https://hooks.zapier.com/..."
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1"
        />
      </Field>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
        Active (run on schedule)
      </label>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting}
          className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 px-3 py-1 rounded"
        >
          {submitting ? "..." : initial ? "Save" : "Create"}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1 rounded text-zinc-400 hover:text-zinc-200">
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-zinc-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
